import { MasterVehicleModel } from '../../services/master/vehicle.interfaces'

// Charter car type is inferred from the master model's size + body type so the
// owner only has to pick brand and model. Returns '' when there is no sensible
// match (e.g. pick-ups), in which case the owner selects the type manually.
const FIRST_CLASS_MODEL = /^(S\d|S_|S-?CLASS|MAYBACH|7\d\d|I7|LS\d)/i

export function inferCarType(
  model: Pick<MasterVehicleModel, 'key' | 'vehiclesize_intl' | 'vehicletype'>,
) {
  const size = (model.vehiclesize_intl || '').toUpperCase()
  const type = (model.vehicletype || '').toUpperCase()
  const isLuxury = size === 'LUXURY'

  switch (type) {
    case 'VAN':
      return isLuxury ? 'Luxury Van' : 'Van'
    case 'MPV':
      return isLuxury || size === 'MPV' ? 'Luxury MPV' : 'SUV'
    case 'SUV':
      return 'SUV'
    case 'SEDAN':
    case 'COUPE':
    case 'CONVERTIBLE':
      if (isLuxury) return FIRST_CLASS_MODEL.test(model.key) ? 'First Class' : 'Business Class'
      if (size === 'INTERMEDIATE' || size === 'FULLSIZE') return 'Standard Sedan'
      return 'Economy Sedan'
    case 'HATCHBACK':
      return isLuxury ? 'Business Class' : 'Economy Sedan'
    default:
      return ''
  }
}

// ── Suggested round-trip (full-day) pricing, THB net revenue ─────────────
// Hardcoded for the Thailand market; owners can override every value.
export const ROUND_TRIP_HOURS = [4, 8, 10, 12]

interface CarTypePricing {
  fullDay8h: number
  overnight: number
}

const PRICING: Record<string, CarTypePricing> = {
  'Economy Sedan': { fullDay8h: 1800, overnight: 800 },
  'Standard Sedan': { fullDay8h: 2200, overnight: 800 },
  SUV: { fullDay8h: 2800, overnight: 1000 },
  Van: { fullDay8h: 2500, overnight: 1000 },
  'Business Class': { fullDay8h: 4500, overnight: 1200 },
  'Luxury MPV': { fullDay8h: 4500, overnight: 1200 },
  'Luxury Van': { fullDay8h: 5500, overnight: 1500 },
  'First Class': { fullDay8h: 8000, overnight: 1500 },
}

const HOUR_FACTOR: Record<number, number> = { 4: 0.6, 8: 1, 10: 1.2, 12: 1.4 }

const pricingFor = (carType: string) => PRICING[carType] ?? PRICING['Economy Sedan']

export function suggestedFullDayPrice(carType: string, hours: number) {
  const raw = pricingFor(carType).fullDay8h * (HOUR_FACTOR[hours] ?? 1)
  return Math.round(raw / 100) * 100
}

export const suggestedOvernightFee = (carType: string) => pricingFor(carType).overnight

// maxHoursPerDay is persisted as e.g. "8 ชั่วโมง" (existing drafts / backend format).
export const formatHoursValue = (hours: number) => `${hours} ชั่วโมง`

export const parseHoursValue = (value: string) => parseInt(value, 10) || 8
